import type { FC } from 'react';

import { useTranslation } from '@mint-lib/i18n';
import { ArrowLeft } from '@mint-lib/icons';
import {
  Button,
  Chip,
  CircularLoadingButton,
  Divider,
  typographyVariants,
} from '@mint-lib/tw-components';
import { StaticDateSpan } from '@mint-lib/tw-components/patterns';
import { cn } from '@mint-lib/tw-utils';
import { Link } from '@mint/routing';

type Props = {
  createdAt: Date;
  actionDisabled: boolean;
  isPending: boolean;
};

const CreateApprovalHeader: FC<Props> = ({
  createdAt,
  actionDisabled,
  isPending,
}) => {
  const { t } = useTranslation('@myn/approval_v2');

  return (
    <div>
      <div className="flex justify-between gap-5">
        <div>
          <Link
            to="/company-profile/flows"
            className={cn(
              typographyVariants({
                variant: 'bodyShort02',
                color: 'link',
              }),
              'item-center flex gap-1',
            )}
            data-dd-action-name="Navigate to the Approval Hub"
          >
            <ArrowLeft fill="currentColor" />
            {t('Back to approval flows page')}
          </Link>
        </div>

        <div>
          <div className="flex items-center gap-2">
            <span
              className={typographyVariants({
                variant: 'caption1',
                color: 'secondary',
              })}
            >
              {t('Creation date')}:
            </span>
            <Chip variant="outline" size="medium" color="silver">
              <StaticDateSpan date={createdAt} />
            </Chip>
            <Divider orientation="vertical" className="h-6!" />
            <Button
              data-dd-action-name="Create new flow"
              variant="contained"
              size="medium"
              color="primary"
              disabled={actionDisabled || isPending}
              type="submit"
            >
              {isPending ? (
                <CircularLoadingButton size="small" variant="contained" />
              ) : null}
              {t('Save')}
            </Button>
          </div>
        </div>
      </div>
      <h2 className={cn(typographyVariants({ variant: 'h2' }))}>
        {t('New flow')}
      </h2>
    </div>
  );
};

export default CreateApprovalHeader;
